import React, { useState, useRef, useEffect, useCallback } from "react";
import LoginPage from "./LoginPage";

// const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "http://127.0.0.1:5000";
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "https://interpreterflask.iugroup.com";


const STORAGE_KEYS = {
  MODE: "chatAppMode",
  MESSAGES: "chatAppMessages",
};

const DATABASE_QUERY_SUGGESTIONS = [
  "In which state is the customer AALBORG INDUSTRIES INC located?",
  "What's the main address of customer Aalborg Industries Inc.?",
  "Is the customer AALBORG INDUSTRIES INC currently considered active?",
  "Does the record for AALBORG INDUSTRIES INC indicate that they have a VOS Video service?",
  "Show all past completed events for customer AALBORG INDUSTRIES INC with language.",
  "Who is the billing customer of event E1220877?",
  "Who is the ordering customer of event E1252955?",
  "Who is the billing contact of event E451998?",
  "Who is the ordering contact of event E451998?",
  "Show assigned interpreter details of event E1220877.",
  "Show me event details of event E1220877.",
  "Show me all upcoming events of interpreter Sonu",
  "Fetch all interpreters whose ZIP code is 10002.",
  "Fetch all interpreters whose street contains '243 Grand Street'.",
];

const GENERAL_QUERY_EXAMPLES = [
  "What are the email addresses for accounting?",
  "Tell me about Regional Centers.",
  "How to enter Amazon requests?",
  "How to fill timesheets?",
  "What are contact inforamtion of IU staff"

];

function App() {
  // ── Auth state ──────────────────────────────────────────────────────────────
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  // Check for existing token on mount
  useEffect(() => {
    const savedToken = localStorage.getItem("chatAppToken");
    const savedEmail = localStorage.getItem("chatAppEmail");
    if (savedToken && savedEmail) {
      setIsLoggedIn(true);
    }
  }, []);

  const handleLoginSuccess = useCallback(() => {
    setIsLoggedIn(true);
  }, []);

  const handleLogout = useCallback(() => {
    localStorage.removeItem("chatAppToken");
    localStorage.removeItem("chatAppEmail");
    localStorage.removeItem("chatAppAccessType");
    sessionStorage.removeItem("chatAppMessages");
    setIsLoggedIn(false);
    setMessages([]);
    setIsInitialized(false);
  }, []);

  // ── Chat state ──────────────────────────────────────────────────────────────
  // Initialize messages as empty - will be loaded from sessionStorage or set to default
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [chatMode, setChatMode] = useState("general"); // "general" or "employee"
  const [chatVoiceActive, setChatVoiceActive] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const messagesEndRef = useRef(null);
  const abortControllerRef = useRef(null);
  const chatRecognitionRef = useRef(null);
  const inputRef = useRef("");
  const suggestionsRef = useRef(null);

  // Load saved mode from localStorage and messages from sessionStorage on mount
  // Also re-runs when isInitialized is reset to false (e.g. after logout)
  useEffect(() => {
    if (isInitialized) return; // already set up, skip

    const savedMode = localStorage.getItem(STORAGE_KEYS.MODE);
    if (savedMode) {
      setChatMode(savedMode);
    }

    // Load messages from sessionStorage (session-based)
    const savedMessages = sessionStorage.getItem(STORAGE_KEYS.MESSAGES);
    if (savedMessages) {
      try {
        const parsedMessages = JSON.parse(savedMessages);
        if (Array.isArray(parsedMessages) && parsedMessages.length > 0) {
          setMessages(parsedMessages);
          setIsInitialized(true);
          return;
        }
      } catch (error) {
        console.error("Error loading messages from sessionStorage:", error);
      }
    }

    // If no saved messages, set default welcome message and general mode card
    const currentMode = savedMode || "general";
    const suggestionsList = currentMode === "general" ? GENERAL_QUERY_EXAMPLES.slice(0, 4) : DATABASE_QUERY_SUGGESTIONS.slice(0, 4);
    const suggestions = suggestionsList
      .map((example) => `• ${example}`)
      .join("<br>");

    const welcomeMessage = {
      sender: "bot",
      text: "Welcome to the <strong>IU AI Assistant</strong>. I can assist with both general inquiries and query from Database."
    };

    const modeCardMessage = {
      sender: "bot",
      text: `
        <div style="
          padding: 18px; 
          border-radius: 12px;
          font-family: 'Segoe UI', sans-serif;
          line-height: 1.7;
          border-left: 4px solid #4a90e2;
        ">
          <div style="font-size: 20px; font-weight: 700; margin-bottom: 10px;">
            🧠 ${currentMode === "general" ? "General Query" : "Query from Database"}
          </div>
          <div style="font-size: 15px; margin-bottom: 14px;">
            I'm here to help. What would you like to do?
          </div>
          <div style="font-weight: 600; margin-bottom: 6px;">Try asking me:</div>
          <div>${suggestions}</div>
        </div>
      `,
    };
    setMessages([welcomeMessage, modeCardMessage]);
    setIsInitialized(true);
  }, [isInitialized]); // re-runs when isInitialized resets to false (after logout)


  // Save mode to localStorage (persistent preference) and messages to sessionStorage (session-based)
  // Only save after initialization to prevent overwriting on mount
  useEffect(() => {
    if (!isInitialized) return;

    localStorage.setItem(STORAGE_KEYS.MODE, chatMode);
    // Save messages to sessionStorage for session-based persistence
    if (messages.length > 0) {
      sessionStorage.setItem(STORAGE_KEYS.MESSAGES, JSON.stringify(messages));
    }
  }, [chatMode, messages, isInitialized]);

  useEffect(() => {
    inputRef.current = input;
  }, [input]);

  // Filter suggestions when user types in any mode
  useEffect(() => {
    if (input.trim().length > 0) {
      const searchTerm = input.trim().toLowerCase();
      const suggestionsList = chatMode === "general"
        ? GENERAL_QUERY_EXAMPLES
        : DATABASE_QUERY_SUGGESTIONS;

      const filtered = suggestionsList.filter(suggestion =>
        suggestion.toLowerCase().includes(searchTerm)
      );
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
      setSelectedSuggestionIndex(-1);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
      setSelectedSuggestionIndex(-1);
    }
  }, [input, chatMode]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(event.target)) {
        const inputElement = event.target.closest('input');
        if (!inputElement || inputElement !== document.querySelector('input[type="text"]')) {
          setShowSuggestions(false);
        }
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
        document.removeEventListener('touchstart', handleClickOutside);
      };
    }
  }, [showSuggestions]);

  // Scroll selected suggestion into view
  useEffect(() => {
    if (selectedSuggestionIndex >= 0 && suggestionsRef.current) {
      const selectedElement = suggestionsRef.current.children[selectedSuggestionIndex];
      if (selectedElement) {
        selectedElement.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
        });
      }
    }
  }, [selectedSuggestionIndex]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading]);

  const pushMessages = useCallback((newMessages) => {
    if (!newMessages) return;
    const messagesArray = Array.isArray(newMessages) ? newMessages : [newMessages];
    setMessages((prev) => [...prev, ...messagesArray]);
  }, []);

  const [micPermissionGranted, setMicPermissionGranted] = useState(false);

  const ensureMicrophonePermission = useCallback(async () => {
    if (micPermissionGranted) {
      return true;
    }

    if (typeof navigator === "undefined") {
      throw new Error("Microphone access is not supported on this device/browser.");
    }

    const legacyGetUserMedia =
      navigator.getUserMedia ||
      navigator.webkitGetUserMedia ||
      navigator.mozGetUserMedia ||
      navigator.msGetUserMedia;

    const requestAudioStream = () => {
      if (navigator.mediaDevices?.getUserMedia) {
        return navigator.mediaDevices.getUserMedia({ audio: true });
      }
      if (legacyGetUserMedia) {
        return new Promise((resolve, reject) => {
          legacyGetUserMedia.call(navigator, { audio: true }, resolve, reject);
        });
      }
      return null;
    };

    const audioPromise = requestAudioStream();

    if (!audioPromise) {
      console.warn("Microphone stream API not available in this browser; falling back to SpeechRecognition permission prompt.");
      setMicPermissionGranted(true);
      return true;
    }

    try {
      const stream = await audioPromise;
      stream.getTracks().forEach((track) => track.stop());
      setMicPermissionGranted(true);
      return true;
    } catch (error) {

      if (error.name === "NotAllowedError" || error.name === "SecurityError") {
        throw new Error("Microphone permission denied. Please allow microphone access in your browser settings and try again.");
      }

      if (error.name === "NotFoundError" || error.name === "DevicesNotFoundError") {
        throw new Error("No microphone was found. Please connect a microphone and try again.");
      }

      if (typeof window !== "undefined" && !window.isSecureContext) {
        const host = window.location?.hostname || "";
        const isLocalHost = ["localhost", "127.0.0.1", "::1"].includes(host);
        if (!isLocalHost) {
          throw new Error("Microphone access requires a secure (HTTPS) connection when running on a public network address. Please access this app via HTTPS (or use localhost) and try again.");
        }
      }

      throw new Error("Unable to access microphone. Please ensure no other app is using it and try again.");
    }
  }, [micPermissionGranted]);

  const stopChatVoiceCapture = useCallback(() => {
    if (chatRecognitionRef.current) {
      chatRecognitionRef.current.onresult = null;
      chatRecognitionRef.current.onerror = null;
      try {
        chatRecognitionRef.current.stop();
      } catch (error) {
        console.warn("Chat voice stop error:", error);
      }
      chatRecognitionRef.current = null;
    }
    setChatVoiceActive(false);
  }, []);

  const sendQuery = useCallback(async (queryText) => {
    const trimmed = queryText.trim();
    if (!trimmed) return;

    pushMessages({ sender: "user", text: trimmed });
    setLoading(true);
    abortControllerRef.current = new AbortController();

    try {
      // const endpoint = chatMode === "general" ? "/generalquery" : "/employeeresponse";
      const endpoint = "/employeeresponse";
      // const endpoint = chatMode === "general" ? "/generalquery" : "/interpreterresponse";

      console.log(`💬 Sending ${chatMode} query to ${endpoint}...`);

      // Read access_type from localStorage (saved at login) and attach to every request
      const storedAccessType = localStorage.getItem("chatAppAccessType");
      const accessType = storedAccessType ? JSON.parse(storedAccessType) : ["scheduler_access", "billing_access"];

      const requestBody = chatMode === "general"
        ? {
          question: trimmed,
          mode: "general",
          context: "General support for interpreter platform users.",
          access_type: accessType,
        }
        : {
          question: trimmed,
          mode: chatMode,
          access_type: accessType,
        };

      const response = await fetch(`${API_BASE_URL}${endpoint}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Unable to process your query right now.");
      }

      pushMessages({ sender: "bot", text: data.response || data.message || "Response received." });
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }
      console.error("❌ Query Error:", error.message);
      pushMessages({
        sender: "bot",
        text: `❌ ${error.message || "Unable to process your query right now."}`,
      });
    } finally {
      setLoading(false);
      abortControllerRef.current = null;
    }
  }, [chatMode, pushMessages]);

  const handleChatVoiceToggle = useCallback(async () => {
    if (chatVoiceActive) {
      stopChatVoiceCapture();
      return;
    }

    try {
      await ensureMicrophonePermission();
    } catch (permissionError) {
      pushMessages({
        sender: "bot",
        text: `❌ ${permissionError.message}`,
      });
      return;
    }

    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      pushMessages({
        sender: "bot",
        text: "❌ Voice input is not supported in this browser.",
      });
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      const transcript = (event.results[0][0].transcript || "").trim();
      if (!transcript) return;

      const base = (inputRef.current || "").trim();
      const combined = base ? `${base} ${transcript}` : transcript;
      setInput("");
      sendQuery(combined);
    };

    recognition.onerror = (event) => {
      console.error("Chat voice input error:", event.error);
      pushMessages({
        sender: "bot",
        text: "❌ Unable to capture voice input. Please try again.",
      });
      stopChatVoiceCapture();
    };

    recognition.onend = () => {
      setChatVoiceActive(false);
      chatRecognitionRef.current = null;
    };

    try {
      recognition.start();
      chatRecognitionRef.current = recognition;
      setChatVoiceActive(true);
    } catch (error) {
      console.error("Chat voice start error:", error);
      pushMessages({
        sender: "bot",
        text: "❌ Unable to start voice input. Please try again.",
      });
    }
  }, [chatVoiceActive, ensureMicrophonePermission, pushMessages, sendQuery, stopChatVoiceCapture]);

  useEffect(() => {
    return () => {
      stopChatVoiceCapture();
    };
  }, [stopChatVoiceCapture]);

  const handleSuggestionSelect = (suggestion) => {
    setInput(suggestion);
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
  };

  const handleInputKeyDown = (e) => {
    if (showSuggestions && filteredSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex(prev =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex(prev => prev > 0 ? prev - 1 : -1);
      } else if (e.key === "Enter" && selectedSuggestionIndex >= 0) {
        e.preventDefault();
        handleSuggestionSelect(filteredSuggestions[selectedSuggestionIndex]);
      } else if (e.key === "Escape") {
        setShowSuggestions(false);
        setSelectedSuggestionIndex(-1);
      } else if (e.key === "Enter" && selectedSuggestionIndex === -1 && input.trim()) {
        handleChatSend();
      }
    } else if (e.key === "Enter" && !loading && input.trim()) {
      handleChatSend();
    }
  };

  const handleChatSend = async () => {
    if (!input.trim()) {
      return;
    }
    const query = input.trim();
    setInput("");
    setShowSuggestions(false);
    setSelectedSuggestionIndex(-1);
    await sendQuery(query);
  };

  const handleModeChange = (mode) => {
    setChatMode(mode);
    const suggestionsList = mode === "general" ? GENERAL_QUERY_EXAMPLES.slice(0, 4) : DATABASE_QUERY_SUGGESTIONS.slice(0, 4);
    const suggestions = suggestionsList
      .map((example) => `• ${example}`)
      .join("<br>");

    pushMessages({
      sender: "bot",
      text: `
        <div style="
          padding: 18px; 
          border-radius: 12px;
          font-family: 'Segoe UI', sans-serif;
          line-height: 1.7;
          border-left: 4px solid #4a90e2;
        ">
          <div style="font-size: 20px; font-weight: 700; margin-bottom: 10px;">
            🧠 ${mode === "general" ? "General Query" : "Query from Database"}
          </div>
          <div style="font-size: 15px; margin-bottom: 14px;">
            I'm here to help. What would you like to do?
          </div>
          <div style="font-weight: 600; margin-bottom: 6px;">Try asking me:</div>
          <div>${suggestions}</div>
        </div>
      `,
    });
  };

  const stopLoading = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setLoading(false);
    pushMessages({
      sender: "bot",
      text: "❌ Request cancelled by user.",
    });
  };

  // ── Gate: show login page until authenticated ─────────────────────────────
  if (!isLoggedIn) {
    return <LoginPage onLoginSuccess={handleLoginSuccess} />;
  }

  return (
    <div className="chat-container" style={styles.chatContainer}>
      <style>{`
        @keyframes bounce {
          0%, 80%, 100% {
            transform: scale(1);
            opacity: 0.5;
          }
          40% {
            transform: scale(1.2);
            opacity: 1;
          }
        }
        
        .loading-dot {
          animation: bounce 1.4s infinite ease-in-out;
        }
        
        .loading-dot:nth-child(1) {
          animation-delay: -0.32s;
        }
        
        .loading-dot:nth-child(2) {
          animation-delay: -0.16s;
        }

        @media (max-width: 1024px) {
          .header-content {
            flex-wrap: wrap;
            gap: 12px;
          }

          .header-actions {
            width: auto;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 12px;
          }

          .chat-container {
            height: auto;
            min-height: 100vh;
          }

          .chat-wrapper {
            padding-bottom: env(safe-area-inset-bottom, 20px);
          }

          .chat-header {
            padding: 12px 18px;
          }

          .chat-body {
            padding: 18px;
          }

          .input-area {
            padding: 14px 18px;
          }
        }

        @media (max-width: 768px) {
          .mode-switcher {
            padding: 3px 6px;
            gap: 5px;
            font-size: 10px;
            flex-wrap: nowrap;
            white-space: nowrap;
          }

          .mode-label {
            font-size: 10px;
            white-space: nowrap;
          }

          .mode-option {
            font-size: 10px;
            gap: 3px;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .mode-radio {
            width: 11px;
            height: 11px;
          }

          .suggestions-dropdown {
            max-height: 180px;
            font-size: 13px;
          }

          .suggestion-item {
            padding: 14px 16px;
            font-size: 13px;
            min-height: 44px;
            display: flex;
            align-items: center;
          }
        }

        @media (max-width: 640px) {
          .header-content {
            gap: 10px;
          }

          .header-actions {
            width: auto;
            flex-wrap: wrap;
            justify-content: flex-start;
            gap: 8px;
          }

          .mode-switcher {
            width: auto;
            flex-wrap: nowrap;
            justify-content: flex-start;
            padding: 3px 6px;
            gap: 4px;
            font-size: 10px;
            white-space: nowrap;
          }

          .mode-label {
            font-size: 10px;
            white-space: nowrap;
          }

          .mode-option {
            font-size: 10px;
            gap: 3px;
            white-space: nowrap;
            flex-shrink: 0;
          }

          .mode-radio {
            width: 10px;
            height: 10px;
          }

          .chat-header {
            padding: 10px 14px;
          }

          .header-title {
            font-size: 18px;
          }

          .chat-body {
            padding: 14px;
            gap: 10px;
          }

          .input-area {
            padding: 12px 14px;
          }

          .suggestions-dropdown {
            max-height: 150px;
            font-size: 12px;
            border-radius: 10px;
            left: 0;
            right: 0;
            width: 100%;
          }

          .suggestion-item {
            padding: 12px 14px;
            font-size: 12px;
            min-height: 44px;
            display: flex;
            align-items: center;
            word-wrap: break-word;
            line-height: 1.4;
          }
        }

        @media (max-width: 480px) {
          .suggestions-dropdown {
            max-height: 120px;
            font-size: 11px;
            border-radius: 8px;
          }

          .suggestion-item {
            padding: 10px 12px;
            font-size: 11px;
            min-height: 40px;
          }
        }

        @media (max-height: 600px) {
          .suggestions-dropdown {
            max-height: 120px;
          }
        }
      `}</style>

      <div className="chat-header" style={styles.chatHeader}>
        <div className="header-content" style={styles.headerContent}>
          <div className="logo-section" style={styles.logoSection}>
            <img src="/iu_logo.png" alt="IU Logo" style={{ width: "40px", height: "40px", borderRadius: "50%" }} />
            <h1 className="header-title" style={styles.headerTitle}><strong>IU AI Assistant</strong></h1>
          </div>
          <div style={styles.headerActions}>
            <div className="mode-switcher" style={styles.modeSwitcher}>
              <span style={styles.modeLabel}>Mode:</span>
              <label style={styles.modeOption}>
                <input
                  type="radio"
                  name="chatMode"
                  value="general"
                  checked={chatMode === "general"}
                  onChange={() => handleModeChange("general")}
                  style={styles.modeRadio}
                />
                General Query
              </label>
              <label style={styles.modeOption}>
                <input
                  type="radio"
                  name="chatMode"
                  value="employee"
                  checked={chatMode === "employee"}
                  onChange={() => handleModeChange("employee")}
                  style={styles.modeRadio}
                />
                Query from Database
              </label>
            </div>
            {/* Logout button */}
            <button
              onClick={handleLogout}
              style={styles.logoutBtn}
              title="Sign out"
            >
              🚪 Sign Out
            </button>
          </div>
        </div>
      </div>

      <div className="chat-wrapper" style={styles.chatWrapper}>
        <div className="chat-body" style={styles.chatBody}>
          {messages.map((msg, i) => (
            <div key={i} style={msg.sender === "user" ? styles.userMsg : styles.botMsg}>
              <div style={styles.msgContent}>
                <div
                  className="message"
                  style={{
                    ...(msg.sender === "user" ? styles.userText : styles.botText),
                    maxWidth: "85vw",
                    overflowX: "auto",
                    overflowY: "auto",
                    maxHeight: "104vh",
                    wordBreak: "break-word",
                    WebkitOverflowScrolling: "touch"
                  }}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              </div>
            </div>
          ))}

          {loading && (
            <div style={styles.botMsg}>
              <div style={styles.loadingContainer}>
                <div style={styles.loadingDots}>
                  <span className="loading-dot" style={styles.loadingDot}></span>
                  <span className="loading-dot" style={styles.loadingDot}></span>
                  <span className="loading-dot" style={styles.loadingDot}></span>
                </div>
                <p style={styles.loadingText}>Processing your request...</p>
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="input-area" style={styles.inputArea}>
          <div className="form-group" style={styles.formGroup}>
            <div style={styles.voiceInputWrapper}>
              <button
                type="button"
                onClick={handleChatVoiceToggle}
                style={{
                  ...styles.chatVoiceBtn,
                  ...(chatVoiceActive ? styles.chatVoiceBtnActive : {})
                }}
                disabled={loading}
                aria-pressed={chatVoiceActive}
                aria-label={chatVoiceActive ? "Stop voice input" : "Start voice input"}
                title={chatVoiceActive ? "Stop voice input" : "Start voice input"}
              >
                <img
                  src="/mic.png"
                  alt="Voice input"
                  style={{
                    ...styles.chatVoiceIcon,
                    ...(chatVoiceActive ? styles.chatVoiceIconActive : {})
                  }}
                />
              </button>
              <input
                type="text"
                value={input}
                placeholder={`Ask your question in ${chatMode === "general" ? "General Query" : "Query from Database"} mode...`}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleInputKeyDown}
                disabled={loading}
                style={styles.chatTextInput}
              />
            </div>
            {showSuggestions && filteredSuggestions.length > 0 && (
              <div ref={suggestionsRef} className="suggestions-dropdown" style={styles.suggestionsDropdown}>
                {filteredSuggestions.map((suggestion, index) => (
                  <div
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionSelect(suggestion)}
                    style={{
                      ...styles.suggestionItem,
                      ...(index === selectedSuggestionIndex ? styles.suggestionItemSelected : {}),
                      ...(index === filteredSuggestions.length - 1 ? { borderBottom: 'none' } : {})
                    }}
                    onMouseEnter={() => setSelectedSuggestionIndex(index)}
                    onTouchStart={() => setSelectedSuggestionIndex(index)}
                  >
                    {suggestion}
                  </div>
                ))}
              </div>
            )}
            {loading ? (
              <button
                onClick={stopLoading}
                style={{ ...styles.sendBtn, ...styles.stopBtnInline }}
              >
                ⏹️ Stop
              </button>
            ) : (
              <button
                onClick={handleChatSend}
                disabled={loading || !input.trim()}
                style={styles.sendBtn}
              >
                Send
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const styles = {
  chatContainer: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100vh',
    backgroundColor: '#f0f2f5',
    fontFamily: '"Segoe UI", Tahoma, Geneva, Verdana, sans-serif',
    overflow: 'hidden',
  },
  chatHeader: {
    position: 'sticky',
    top: 0,
    background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 100%)',
    color: '#000',
    padding: '16px 24px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    zIndex: 100,
  },
  headerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    maxWidth: '1200px',
    margin: '0 auto',
    width: '100%',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  modeSwitcher: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    backgroundColor: '#fff',
    padding: '4px 8px',
    borderRadius: '20px',
    border: '1px solid #d3e2ff',
    boxShadow: '0 1px 4px rgba(30,91,168,0.12)',
    fontSize: '11px',
    flexWrap: 'nowrap',
    whiteSpace: 'nowrap',
  },
  modeLabel: {
    fontSize: '11px',
    fontWeight: 600,
    color: '#1e5ba8',
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    whiteSpace: 'nowrap',
  },
  modeOption: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    fontSize: '11px',
    color: '#1e1e1e',
    fontWeight: 500,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    flexShrink: 0,
  },
  modeRadio: {
    accentColor: '#1e5ba8',
    width: '12px',
    height: '12px',
    cursor: 'pointer',
  },
  logoSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  headerTitle: {
    margin: 0,
    fontSize: '20px',
    fontWeight: '700',
  },
  chatWrapper: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'auto',
  },
  chatBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  userMsg: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '8px',
  },
  botMsg: {
    display: 'flex',
    gap: '12px',
    marginBottom: '8px',
  },
  msgContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  userText: {
    backgroundColor: '#1e5ba8',
    color: 'white',
    padding: '12px 16px',
    borderRadius: '16px',
    maxWidth: '70%',
    wordWrap: 'break-word',
    lineHeight: '1.4',
  },
  botText: {
    backgroundColor: 'white',
    color: '#333',
    padding: '12px 16px',
    borderRadius: '16px',
    maxWidth: '70%',
    wordWrap: 'break-word',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
    lineHeight: '1.4',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: 'white',
    padding: '20px 24px',
    borderRadius: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
  },
  loadingDots: {
    display: 'flex',
    gap: '8px',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    backgroundColor: '#1e5ba8',
  },
  loadingText: {
    margin: '0',
    fontSize: '14px',
    color: '#666',
    fontWeight: '500',
  },
  inputArea: {
    padding: '16px 24px',
    backgroundColor: 'white',
    borderTop: '1px solid #e0e0e0',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  formGroup: {
    display: 'flex',
    gap: '10px',
    maxWidth: '100%',
    width: '100%',
    alignItems: 'center',
    flexWrap: 'nowrap',
    position: 'relative',
  },
  voiceInputWrapper: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    border: '2px solid #e0e0e0',
    borderRadius: '24px',
    padding: '0 8px 0 4px',
    backgroundColor: '#fff',
    transition: 'border-color 0.3s, box-shadow 0.3s',
  },
  chatVoiceBtn: {
    border: 'none',
    background: 'transparent',
    color: '#1e5ba8',
    fontSize: '20px',
    cursor: 'pointer',
    padding: '0 10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    width: '40px',
    borderRadius: '50%',
    transition: 'background-color 0.3s, color 0.3s',
  },
  chatVoiceBtnActive: {
    backgroundColor: '#ffe2e2',
    borderColor: '#ff7b7b',
    boxShadow: '0 0 0 3px rgba(255,123,123,0.2)',
  },
  chatVoiceIcon: {
    width: '18px',
    height: '18px',
    objectFit: 'contain',
    pointerEvents: 'none',
    filter: 'brightness(0) saturate(100%) invert(17%) sepia(87%) saturate(1441%) hue-rotate(184deg) brightness(92%) contrast(99%)',
    transition: 'filter 0.3s',
  },
  chatVoiceIconActive: {
    filter: 'invert(23%) sepia(94%) saturate(2995%) hue-rotate(353deg) brightness(99%) contrast(103%)',
  },
  chatTextInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '14px',
    padding: '12px 8px',
    fontFamily: 'inherit',
    backgroundColor: 'transparent',
  },
  sendBtn: {
    backgroundColor: '#1e5ba8',
    color: 'white',
    border: 'none',
    padding: '12px 28px',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.3s',
  },
  stopBtnInline: {
    backgroundColor: '#ff6b6b',
  },
  suggestionsDropdown: {
    position: 'absolute',
    bottom: 'calc(100% + 8px)',
    left: 0,
    right: 0,
    maxHeight: '200px',
    overflowY: 'auto',
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '12px',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    zIndex: 1000,
    width: '100%',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
  },
  suggestionItem: {
    padding: '12px 16px',
    cursor: 'pointer',
    fontSize: '14px',
    color: '#333',
    borderBottom: '1px solid #f0f0f0',
    transition: 'background-color 0.2s',
    touchAction: 'manipulation',
    userSelect: 'none',
    WebkitTapHighlightColor: 'transparent',
    wordWrap: 'break-word',
    lineHeight: '1.4',
  },
  suggestionItemSelected: {
    backgroundColor: '#e9f1ff',
    color: '#1e5ba8',
  },
  logoutBtn: {
    background: 'none',
    border: '1.5px solid #d3e2ff',
    borderRadius: '20px',
    padding: '6px 14px',
    fontSize: '13px',
    fontWeight: 500,
    color: '#1e5ba8',
    cursor: 'pointer',
    transition: 'background 0.2s, color 0.2s',
    whiteSpace: 'nowrap',
  }
};

export default App;
